import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import moment from 'moment';
import classnames from 'classnames';
import ms from 'ms';

import { SR_API_ACTION_UTILITY_PARTNER_LISTING_FEED_SYNC } from 'api/utilityActionTypes';

import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREEN_BRIGHTER,
} from 'client/components/SrButton/SrButton';
import UpSelfOnboardingHeader from 'client/components/UPSelfOnboarding/UpSelfOnboardingHeader';

import './UpSelfOnboarding.less';

const ADDRESS_COUNT_LIMIT = 6;

const UP_SELF_ONBOARDING_SYNC_STATE_INIT = 'init';
const UP_SELF_ONBOARDING_SYNC_STATE_LOADING = 'loading';
const UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED = 'completed';

const BUTTON_TEXT_TO_SYNC_STATE_MAPPING = {
  [UP_SELF_ONBOARDING_SYNC_STATE_INIT]: 'Start Syncing',
  [UP_SELF_ONBOARDING_SYNC_STATE_LOADING]: 'Syncing...',
  [UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED]: 'Continue',
};

const getFormFields = ({ onChangeUrl, selfOnboardingSyncState } = {}) => {
  const formFields = [
    {
      id: 'url',
      title: 'Public URL to your available listings',
      label: (
        <>
          <SrText weight="500" lineHeight="24px">
            Enter the url that lists your rental vacancies. (Example: yourwebsite.com/
            <SrText weight="700" lineHeight="24px" color="orange300">
              available-listings
            </SrText>
            )
          </SrText>
        </>
      ),
      type: 'short_text',
      properties: {
        placeholder: 'Enter url here...',
        onChange: onChangeUrl,
        className:
          selfOnboardingSyncState !== UP_SELF_ONBOARDING_SYNC_STATE_INIT
            ? classnames('upSelfOnboardingUrlInput', {
                completed: selfOnboardingSyncState === UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED,
              })
            : null,
      },
      validations: ['required', 'url', 'nonRealEstatePlatformUrl'],
    },
  ];
  return formFields;
};

const UpSelfOnboardingSyncStep = ({
  activeIndex,
  utilityPartner,
  utilityPartnerGroup,
  onNext,
  onNavigateToStep,
}) => {
  const dispatch = useDispatch();
  const [syncState, setSyncState] = useState(UP_SELF_ONBOARDING_SYNC_STATE_INIT);
  const [scrapeResult, setScrapeResult] = useState(null);

  const formFields = getFormFields();
  const theme = utilityPartnerGroup?.defaultTheme || {};

  const onSubmit = ({ url }) => {
    setSyncState(UP_SELF_ONBOARDING_SYNC_STATE_LOADING);
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_LISTING_FEED_SYNC,
      payload: {
        url,
        utilityPartnerId: utilityPartner.id,
      },
      timeout: ms('2m'),
      onSuccess: (scrapeResult) => {
        setScrapeResult(scrapeResult);
        setSyncState(UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED);
      },
      onError: () => {
        setSyncState(UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED);
      },
    });
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: formFields,
        defaultValues: {
          url: '',
        },
      })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          onChangeUrl: (event) => {
            formikBag.setFieldValue('url', event.target.value);
            setSyncState(UP_SELF_ONBOARDING_SYNC_STATE_INIT);
            setScrapeResult(null);
          },
          selfOnboardingSyncState: syncState,
        });

        const onClick = () => {
          switch (syncState) {
            case UP_SELF_ONBOARDING_SYNC_STATE_INIT:
              formikBag.handleSubmit();
              break;
            case UP_SELF_ONBOARDING_SYNC_STATE_LOADING:
              break;
            case UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED:
              onNext();
              break;
            default:
              break;
          }
        };

        const noResults = !scrapeResult?.addresses?.length;

        return (
          <div styleName="layout">
            <div styleName="left">
              <UpSelfOnboardingHeader
                activeIndex={activeIndex}
                onNavigateToStep={onNavigateToStep}
                partnerGroupLogoUrl={utilityPartnerGroup?.logoUrl}
                partnerGroupPrimaryColor={theme.primaryColor}
                partnerGroupSecondaryColor={theme.secondaryColor}
                partnerGroupInactiveColor={theme.inactiveTextColor}
              />
              <div styleName="lineSeparator" />
              <Form styleName="form">
                <span styleName="titleText">Let's magically sync your site.</span>
                <SrBox margin="26px 0 0">
                  {formFields.map((formField, index) => (
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                      margin={index === 0 ? '0' : null}
                    />
                  ))}
                </SrBox>
                {syncState === UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED && (
                  <SrBox margin={`${noResults ? '0' : '6px'} 0 6px`}>
                    <div styleName={classnames('buttonSync', { noResults })}>
                      <span>{noResults ? 'No listings found.' : 'Sync Completed!'}</span>
                    </div>
                    <SrBox marginTop="15px">
                      <SrText weight="500" lineHeight="20px">
                        {'Not working? '}
                        <SrText
                          weight="500"
                          color="blue300"
                          decoration="underline"
                          cursor="pointer"
                          onClick={() => {
                            formikBag.setFieldValue('url', '');
                            setSyncState(UP_SELF_ONBOARDING_SYNC_STATE_INIT);
                          }}
                        >
                          Try another url
                        </SrText>
                        {!noResults && (
                          <>
                            {' or '}
                            <SrText
                              weight="500"
                              color="blue300"
                              decoration="underline"
                              cursor="pointer"
                              onClick={onNext}
                            >
                              skip for now
                            </SrText>
                            .
                          </>
                        )}
                      </SrText>
                    </SrBox>
                  </SrBox>
                )}
                <SrBox marginTop="21px">
                  <SrButton
                    theme={
                      syncState === UP_SELF_ONBOARDING_SYNC_STATE_LOADING
                        ? SR_BUTTON_THEME_ADOBE_GREEN_BRIGHTER
                        : SR_BUTTON_THEME_ADOBE_GREEN
                    }
                    hasBoxShadow
                    onClick={onClick}
                    themeOverrides={{
                      backgroundColor: theme?.buttonPrimaryColor,
                      color: theme?.buttonPrimaryTextColor,
                    }}
                  >
                    {noResults && syncState === UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED
                      ? 'Skip for now, no active listings'
                      : BUTTON_TEXT_TO_SYNC_STATE_MAPPING[syncState]}
                  </SrButton>
                </SrBox>
              </Form>
            </div>
            <div styleName="right">
              <div styleName="content syncStep fadeIn">
                {syncState === UP_SELF_ONBOARDING_SYNC_STATE_INIT && (
                  <>
                    <div styleName="row">
                      <span styleName="titleText">Your rentals:</span>
                      <img
                        src="/images/up-self-onboarding-sync-table.png"
                        alt="Table"
                        styleName="tableImg"
                      />
                    </div>
                    <div styleName="row">
                      <div styleName="arrow" />
                    </div>
                    <div styleName="row">
                      <span styleName="titleText">Sync daily to Utility Profit:</span>
                      <img
                        src="/images/up-self-onboarding-sync-page.png"
                        alt="Table"
                        styleName="tableImg"
                      />
                    </div>
                  </>
                )}
                {syncState === UP_SELF_ONBOARDING_SYNC_STATE_LOADING && (
                  <>
                    <div styleName="row">
                      <span styleName="titleText">Syncing website...</span>
                      <div styleName="loadingBox">
                        <video
                          width="100%"
                          height="70px"
                          controls={false}
                          src="/videos/website-syncing-listings-animation.mp4"
                          autoPlay
                        >
                          <track src="" kind="captions" />
                        </video>
                      </div>
                    </div>
                  </>
                )}
                {syncState === UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED &&
                  scrapeResult?.addresses?.length > 0 && (
                    <>
                      <div styleName="row">
                        <span styleName="titleText">Sync Completed!</span>

                        <div styleName="table">
                          <div styleName="tableContent">
                            <div styleName="tableRow">
                              <div styleName="tableCell">
                                <span styleName="title">Address</span>
                              </div>
                              <div styleName="tableCell">
                                <span styleName="title">Synced</span>
                              </div>
                            </div>
                            {scrapeResult?.addresses
                              ?.slice(0, ADDRESS_COUNT_LIMIT)
                              .map((address) => {
                                return (
                                  <div key={address.id} styleName="tableRow">
                                    <div styleName="tableCell">
                                      <div>
                                        <span styleName="addressStreet">
                                          {address.streetDisplay}
                                        </span>{' '}
                                        <span styleName="address">{address.city}</span>
                                        {', '}
                                        <span styleName="address">{address.stateCode}</span>{' '}
                                        <span styleName="address">{address.zip}</span>
                                      </div>
                                    </div>
                                    <div styleName="tableCell">
                                      <span styleName="date">{moment().format('MMM D, YYYY')}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            {scrapeResult?.addresses?.length > ADDRESS_COUNT_LIMIT && (
                              <div styleName="tableRowMore">
                                <span>
                                  +{scrapeResult.addresses.length - ADDRESS_COUNT_LIMIT} listings
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                {syncState === UP_SELF_ONBOARDING_SYNC_STATE_COMPLETED &&
                  !scrapeResult?.addresses?.length && (
                    <div styleName="row">
                      <span styleName="titleText">No listings found:</span>
                      <div styleName="noResultsBox">
                        <SrText size="40">⚠️</SrText>
                        <SrText font="greycliff" weight="800" size="20" padding="16px 0 10px">
                          Please make sure your link is correct.
                        </SrText>
                        <SrText
                          font="greycliff"
                          weight="800"
                          size="20"
                          align="center"
                          lineHeight="30px"
                          color="green"
                        >
                          If you don’t have active listings right <br />
                          now, then you can continue.
                        </SrText>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

UpSelfOnboardingSyncStep.displayName = 'UpSelfOnboardingSyncStep';

UpSelfOnboardingSyncStep.propTypes = {
  activeIndex: PropTypes.number,
  utilityPartner: PropTypes.object.isRequired,
  utilityPartnerGroup: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  onNavigateToStep: PropTypes.func,
};

export default UpSelfOnboardingSyncStep;
